import React from 'react';
import classNames from 'classnames';
import { Box, Typography } from '@mui/material';
import Breadcrumbs from '../Breadcrumbs';
import { mapPaths } from '../../utils/urls/mapPaths';
import { LocationView } from '../../types/locationView';
import styles from './GeonameMapHeader.module.scss';

interface Props {
  isBuoys?: boolean;
  isWindStations?: boolean;
  locationView: LocationView;
  hasData: boolean;
  loading: boolean;
}

export const generateDescription = ({
  isBuoys,
  isWindStations,
}: {
  isBuoys?: boolean;
  isWindStations?: boolean;
}) => {
  const defaultDescription = 'Surf Reports & Cams';
  if (isBuoys) {
    return 'Buoys';
  }
  if (isWindStations) {
    return 'Wind Stations';
  }
  return defaultDescription;
};

export const generateBaseUrl = ({
  isBuoys,
  isWindStations,
}: {
  isBuoys?: boolean;
  isWindStations?: boolean;
}) => {
  if (isBuoys) {
    return mapPaths.geonameBuoysBase;
  }
  if (isWindStations) {
    return mapPaths.geonameWindstationBase;
  }
  return mapPaths.geonameBase;
};

const GeonameMapHeader: React.FC<Props> = ({
  locationView,
  isBuoys,
  isWindStations,
  hasData,
  loading,
}) => {
  const name = locationView?.taxonomy?.name || '';
  const description = generateDescription({ isBuoys, isWindStations });
  const baseUrl = generateBaseUrl({ isBuoys, isWindStations });

  return hasData ? (
    <div data-testid="geoname-map-header" className={styles.geonameMapHeader}>
      <Breadcrumbs
        breadcrumbs={locationView.breadCrumbs}
        baseUrl={baseUrl}
        className={classNames(styles.breadcrumbs)}
      />
      <h1>
        {name}
        {description ? ` ${description}` : ''}
      </h1>
    </div>
  ) : (
    <Box
      className={classNames(styles.noData, loading ? styles.loading : '')}
      data-testid="geoname-map-header-no-data"
    >
      <Typography variant="headline" component="h1">
        No {isBuoys ? 'buoys' : 'wind stations'} found
      </Typography>
      <Typography variant="footnote" component="p" color="secondary" lineHeight="22px">
        Try zooming out or moving map.
      </Typography>
    </Box>
  );
};

export default GeonameMapHeader;
